import React, { useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Typography,
    Tooltip

} from '@mui/material';
import SubCard from 'src/components/cards/SubCard';
import Avatar from 'src/components/extended/Avatar';
import ApexBarChart from 'src/components/charts/ApexBarChart';
import UserCountCard from 'src/components/cards/UserCountCard';
import PeopleIcon from '@mui/icons-material/People';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import { useTheme } from '@mui/material/styles';
import axios from 'src/utils/axios';
import ServiceInformationSkeleton from 'src/components/skeleton/ServiceInformationSkeleton';
import { useNavigate } from 'react-router-dom';

const ServiceInformation = ({ ServiceShopId }) => {
    const theme = useTheme();
    const [serviceInformationData, setServiceInformationData] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [serviceOwnersData, setServiceOwnersData] = React.useState([]);
    const [serviceStatisticsData, setServiceStatisticsData] = React.useState({});
    const [apexBarChartCategory, setApexBarChartCategory] = React.useState([]);
    const [apexBarChartSeries, setApexBarChartSeries] = React.useState([{ name: '', data: [] }]);
    const [roleTypes, setRoleTypes] = React.useState(0);
    const navigate = useNavigate();
    const selectedBolge = serviceInformationData.TopServiceShopCode;
    const selectedServis = serviceInformationData.ServiceShopId;

    useEffect(() => {
        setLoading(true);

        // Define promises outside of useEffect
        const promises = [
            getServiceInformationDataPromise(),
            getServiceOwnersDataPromise(),
            getServiceStatisticsDataPromise(),
            getEmployeeManagementRole()
        ];

        Promise.all(promises)
            .then(([serviceInformationData, serviceOwnersData, { serviceStatisticsDatas, categoryData, seriesData }]) => {
                setServiceInformationData(serviceInformationData);
                setServiceOwnersData(serviceOwnersData);
                setServiceStatisticsData(serviceStatisticsDatas);
                setApexBarChartCategory(categoryData);
                setApexBarChartSeries(seriesData);
            })
            .catch((error) => console.error(error))
            .finally(() => setLoading(false));

    }, []); // Dependency array is empty to run useEffect only once

    // Promises defined as functions to avoid recreating them on each render
    const getServiceInformationDataPromise = () => {
        return new Promise((resolve, reject) => {
            axios.get('GetServiceInformationData/' + ServiceShopId)
                .then((response) => resolve(response.data[0]))
                .catch(reject);
        });
    };

    const getServiceOwnersDataPromise = () => {
        return new Promise((resolve, reject) => {
            axios.get('GetServiceOwnersData/' + ServiceShopId)
                .then((response) => resolve(response.data))
                .catch(reject);
        });
    };

    const getEmployeeManagementRole = () => {
        return axios.get(`GetEmployeeManagementRole`)
            .then((response) => {
                if (response.data && response.data.length > 0) {
                    setRoleTypes(response.data[0]);
                } else {
                    setRoleTypes(0);
                }
            });
    }

    const getServiceStatisticsDataPromise = () => {
        return new Promise((resolve, reject) => {
            axios.get('GetServiceStatisticsData/' + ServiceShopId)
                .then((response) => {
                    const serviceStatisticsDatas = response.data;
                    const categoryData = response.data.ServiceShopEmployeeStatistics.map((item) => item.ServiceShopEmloyeeType);

                    // Sorting categoryData
                    categoryData.sort((a, b) => {
                        // Helper function to get the count of a category type
                        const getCount = (category) => {
                            const statistic = serviceStatisticsDatas.ServiceShopEmployeeStatistics.find(item => item.ServiceShopEmloyeeType === category);

                            return statistic ? statistic.ServiceShopEmloyeeTypeCount : 0;
                        };

                        // Priority order based on counts and alphabetical order
                        const priorityOrder = {
                            'Servis Sahibi': 1,
                            'Servis Ortağı': 2,
                            'Servis Yöneticisi': 3,
                            // Add other categories here if needed
                        };

                        // Get counts for comparison
                        const countA = getCount(a);
                        const countB = getCount(b);

                        // Compare based on priority and alphabetically if counts are equal
                        if (countA !== 0 && countB === 0) {
                            return -1; // 'a' should come before 'b'
                        } else if (countA === 0 && countB !== 0) {
                            return 1; // 'b' should come before 'a'
                        } else if (countA !== 0 && countB !== 0) {
                            // Both have non-zero counts, compare based on priority
                            if (priorityOrder[a] !== undefined && priorityOrder[b] !== undefined) {
                                return priorityOrder[a] - priorityOrder[b];
                            }
                            else {
                                if (priorityOrder[a] !== undefined) {
                                    return -1;
                                }
                                if (priorityOrder[b] !== undefined) {
                                    return 1;
                                }
                                return a.localeCompare(b);
                            }

                        } else {
                            // Both have zero counts, compare alphabetically
                            return a.localeCompare(b);
                        }
                    });

                    // Extracting sorted series data for chart
                    const seriesData = [{
                        name: 'Çalışan Sayısı',
                        data: categoryData.map(category => {
                            const statistic = serviceStatisticsDatas.ServiceShopEmployeeStatistics.find(item => item.ServiceShopEmloyeeType === category);
                            return statistic ? statistic.ServiceShopEmloyeeTypeCount : 0;
                        })
                    }];
                    resolve({ serviceStatisticsDatas, categoryData, seriesData });
                })
                .catch(reject);
        });
    };

    const handleEmployeeCardClick = () => {
        const redirectUrl = `/EmployeeMain/1?selectedBolge=${selectedBolge}&selectedServis=${selectedServis}`;
        navigate(redirectUrl);
    };

    const handleVehicleCardClick = () => {
        const redirectUrl = `/ServiceManagement?IsRedirected=true`;
        navigate(redirectUrl);
    };

    return (
        <>
            {loading ? <ServiceInformationSkeleton /> :
                <Grid container spacing={2}>

                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <SubCard
                            title={serviceInformationData.ServiceShopFullName + ' Bilgileri'}
                            style={{ minHeight: 400 }}
                        >
                            <Grid container spacing={2}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12
                                    }}>
                                    <Grid container spacing={3} justifyContent="center" alignItems="center">
                                        <Grid
                                            size={{
                                                xs: 12,
                                                sm: 12,
                                                md: 12
                                            }}>
                                            <Typography variant="h6" gutterBottom style={{ textAlign: 'center' }}>
                                                Servis Sahibi ve Ortakları
                                            </Typography>
                                        </Grid>
                                        {serviceOwnersData.length > 0 && serviceOwnersData.map((item) => (
                                            <Grid key={`${item.ServiceShopOwnerType}-${item.ServiceShopOwnerFullName}`}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexDirection: 'column',
                                                }}>
                                                    <Avatar
                                                        alt={item.ServiceShopOwnerType + ' - ' + item.ServiceShopOwnerFullName}
                                                        src={item.ServiceShopOwnerImage}
                                                        sx={{ width: 100, height: 100 }}
                                                    />
                                                    <Typography variant="h6" gutterBottom style={{ marginTop: '4px', marginBottom: '1px' }}>
                                                        {item.ServiceShopOwnerType}
                                                    </Typography>
                                                    <Typography variant="body2" gutterBottom>
                                                        {item.ServiceShopOwnerFullName}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Bölge Kodu"
                                        value={serviceInformationData.TopServiceShopCode}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 8
                                    }}>
                                    <TextField
                                        label="Bölge Adı"
                                        value={serviceInformationData.TopServiceShopName}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Servis Kodu"
                                        value={serviceInformationData.ServiceShopCode}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}

                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 8
                                    }}>
                                    <TextField
                                        label="Servis Adı"
                                        value={serviceInformationData.ServiceShopName}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Servis Tipi"
                                        value={`${serviceInformationData.ServiceShopType}${serviceInformationData.MainBranchServiceShopFullName ? ' - Şube' : ''}`}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                {serviceInformationData.MainBranchServiceShopFullName && (
                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 12,
                                            md: 8
                                        }}>
                                        <TextField
                                            label="Merkez Servis Bilgisi"
                                            value={serviceInformationData.MainBranchServiceShopFullName}
                                            fullWidth
                                            disabled={true}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                                },
                                                '& .MuiInputLabel-shrink.Mui-disabled': {
                                                    color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                                },
                                            }}
                                        />
                                    </Grid>
                                )}
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: serviceInformationData.MainBranchServiceShopFullName ? 12 : 8
                                    }}>
                                    <Tooltip title={serviceInformationData.ServiceShopCompanyName} arrow>
                                        <TextField
                                            label="Servis Ünvanı"
                                            value={serviceInformationData.ServiceShopCompanyName}
                                            fullWidth
                                            disabled={true}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                                },
                                                '& .MuiInputLabel-shrink.Mui-disabled': {
                                                    color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                                },
                                            }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Açılış Tarihi"
                                        value={serviceInformationData.ServiceShopOpeningDate}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                {/* 
                                   ***********BERAT HESAPLANINCA AÇILACAK****************
                                <Grid item xs={12} sm={12} md={4}>
                                    <TextField
                                        label="Berat (YIL)"
                                        value={serviceInformationData.ServiceShopBerat}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid> 
                                ***********BERAT HESAPLANINCA AÇILACAK****************
                                */}
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 8
                                    }}>
                                    <TextField
                                        label={serviceInformationData.ServiceShopSizeLocationYear
                                            ? `Büyüklük Konumu (${serviceInformationData.ServiceShopSizeLocationYear})`
                                            : 'Büyüklük Konumu'}
                                        value={serviceInformationData.ServiceShopSizeLocation ?? ''}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12
                                    }}>
                                    <Tooltip title={serviceInformationData.ServiceShopAddress} arrow>
                                        <TextField
                                            label="Servis Adresi"
                                            value={serviceInformationData.ServiceShopAddress}
                                            fullWidth
                                            disabled={true}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                                },
                                                '& .MuiInputLabel-root.Mui-disabled': {
                                                    color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                                },
                                                '& .MuiInputLabel-shrink.Mui-disabled': {
                                                    color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                                },
                                            }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="İl"
                                        value={serviceInformationData.ServiceShopCity}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="İlçe"
                                        value={serviceInformationData.ServiceShopDistrict}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="E-Posta"
                                        value={serviceInformationData.ServiceShopEmail}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Yönetici Mail"
                                        value={serviceInformationData.ServiceShopManagerEmail}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Tel 1"
                                        value={serviceInformationData.ServiceShopTel1}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Tel 2"
                                        value={serviceInformationData.ServiceShopTel2}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Fax"
                                        value={serviceInformationData.ServiceShopFax}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Vergi Dairesi"
                                        value={serviceInformationData.ServiceShopTaxOffice}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 4
                                    }}>
                                    <TextField
                                        label="Vergi No"
                                        value={serviceInformationData.ServiceShopTaxNo}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12
                                    }}>
                                    <TextField
                                        label="Servis Yönetmeni"
                                        value={serviceInformationData.ServiceCustodian}
                                        fullWidth
                                        disabled={true}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-root.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                            },
                                            '& .MuiInputLabel-shrink.Mui-disabled': {
                                                color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                            sm: 12,
                            md: 6
                        }}>
                        <SubCard title="Servis İstatistikleri">
                            <Grid container spacing={1}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12
                                    }}>
                                    <Box >
                                        <ApexBarChart category={apexBarChartCategory || []} data={apexBarChartSeries || []} roleTypes={roleTypes.SERVIS_GOREBILIR} selectedBolge={selectedBolge} selectedServis={selectedServis} />
                                    </Box>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12
                                    }}>
                                    <Grid container spacing={3}>
                                        <Grid
                                            style={{ textAlign: "center", cursor: "pointer" }}
                                            onClick={handleEmployeeCardClick}
                                            size={{
                                                xs: 12,
                                                sm: 12,
                                                md: 12
                                            }}>
                                            <UserCountCard
                                                primary="Çalışan Sayısı"
                                                secondary={serviceStatisticsData.ServiceShopTotalEmployee || 0}
                                                iconPrimary={PeopleIcon}
                                                color={'#FF9800'}
                                            />
                                        </Grid>
                                        <Grid
                                            style={{ textAlign: "center", cursor: "pointer" }}
                                            onClick={handleVehicleCardClick}
                                            size={{
                                                xs: 12,
                                                sm: 12,
                                                md: 12
                                            }}>
                                            <UserCountCard
                                                primary="Araç Sayısı"
                                                secondary={serviceStatisticsData.ServiceShopTotalVehicle || 0}
                                                iconPrimary={DriveEtaIcon}
                                                color={'#FF9800'}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </SubCard>
                        {serviceInformationData.ServiceTransferCode ? (
                            <Grid>
                                <SubCard title="Servis Devir Kodları">
                                    <Grid
                                        size={{
                                            xs: 12,
                                            sm: 12,
                                            md: 12
                                        }}>
                                        <Tooltip title={serviceInformationData.ServiceTransferCode} arrow>
                                            <TextField
                                                label="Devir Kodu"
                                                value={serviceInformationData.ServiceTransferCode}
                                                fullWidth
                                                disabled={true}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{
                                                    '& .MuiInputBase-input.Mui-disabled': {
                                                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)', // Text rengini aktif gibi yapar
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'rgba(0, 0, 0, 0.23)', // Kenar rengini aktif gibi yapar
                                                    },
                                                    '& .MuiInputLabel-root.Mui-disabled': {
                                                        color: 'rgba(0, 0, 0, 0.87)', // Label rengini aktif gibi yapar
                                                    },
                                                    '& .MuiInputLabel-shrink.Mui-disabled': {
                                                        color: 'rgba(0, 0, 0, 0.87)', // Shrinked label rengini aktif gibi yapar
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                    </Grid>
                                </SubCard>
                            </Grid>
                        ) : null}

                    </Grid>
                </Grid >
            }
        </>
    );
};

export default ServiceInformation;